import { Action, createReducer, on } from '@ngrx/store';

import * as BookmarkProblemListActions from '../actions/bookmark-problem-list.actions';
import { AddProblemToBookmarkProblemListResponse } from '../models/add-problem-to-bookmark-problem-list';
import { DeleteProblemFromBookmarkProblemListResponse } from '../models/delete-problem-from-bookmark-problem-list';
import { CreateBookmarkProblemListResponse } from '../models/create-bookmark-problem-list';
import { DeleteBookmarkProblemListResponse } from '../models/delete-bookmark-problem-list';
import { BookmarkProblemListTableData } from '../models/bookmark-problem-list';
import { UpdateBookmarkProblemListResponse } from '../models/update-bookmark-problem-list';
import { AppError } from '../errors/app-error';
import { CopyBookmarkProblemListResponse } from '../models/copy-bookmark-problem-list';
import { GeneralProblems } from '../models/problem';

export const bookmarkProblemListFeatureKey = 'bookmarkProblemList';

export interface CreateBookmarkProblemListResult {
  success: boolean;
  response: CreateBookmarkProblemListResponse | null;
  error: AppError | null;
}
export interface UpdateBookmarkProblemListResult {
  success: boolean;
  response: UpdateBookmarkProblemListResponse | null;
  error: AppError | null;
}
export interface CopyBookmarkProblemListResult {
  success: boolean;
  response: CopyBookmarkProblemListResponse | null;
  error: AppError | null;
}
export interface DeleteBookmarkProblemListResult {
  success: boolean;
  response: DeleteBookmarkProblemListResponse | null;
  error: AppError | null;
}

export interface AddProblemToBookmarkProblemListResult {
  success: boolean;
  response: AddProblemToBookmarkProblemListResponse | null;
  error: AppError | null;
}
export interface DeleteProblemFromBookmarkProblemListResult {
  success: boolean;
  response: DeleteProblemFromBookmarkProblemListResponse | null;
  error: AppError | null;
}
export interface ReorderBookmarkProblemListProblemsResult {
  success: boolean;
}

export interface State {
  bookmarkProblemLists: BookmarkProblemListTableData[] | null;
  sharedBookmarkProblemLists: BookmarkProblemListTableData[] | null;
  createBookmarkProblemListResult: CreateBookmarkProblemListResult | null;
  addProblemToBookmarkProblemListResult: AddProblemToBookmarkProblemListResult | null;
  deleteProblemFromBookmarkProblemListResult: DeleteProblemFromBookmarkProblemListResult | null;

  matchedBookmarkProblemListProblemIds: string[] | null;
  isBookmarkProblemListProblemIdsSearching: boolean;
  updateBookmarkProblemListResult: UpdateBookmarkProblemListResult | null;
  copyBookmarkProblemListResult: CopyBookmarkProblemListResult | null;
  deleteBookmarkProblemListResult: DeleteBookmarkProblemListResult | null;
  bookmarkProblemListProblemsSearching: boolean | null;
  bookmarkProblemListProblems: GeneralProblems | null;
  reorderBookmarProblemListProblemsResult: ReorderBookmarkProblemListProblemsResult | null;
}

export const initialState: State = {
  bookmarkProblemLists: null,
  sharedBookmarkProblemLists: null,
  createBookmarkProblemListResult: null,
  addProblemToBookmarkProblemListResult: null,
  deleteProblemFromBookmarkProblemListResult: null,

  matchedBookmarkProblemListProblemIds: null,
  isBookmarkProblemListProblemIdsSearching: null,
  updateBookmarkProblemListResult: null,
  copyBookmarkProblemListResult: null,
  deleteBookmarkProblemListResult: null,
  bookmarkProblemListProblemsSearching: null,
  bookmarkProblemListProblems: null,
  reorderBookmarProblemListProblemsResult: null
};

const BookmarkProblemListReducer = createReducer(
  initialState,

  // Find My Bookmark Problem Lists -----------------------------------------------------------------
  on(BookmarkProblemListActions.findMyBookmarkProblemListsSuccess, (state, { bookmarkProblemLists }) => ({
    ...state,
    bookmarkProblemLists
  })),
  on(BookmarkProblemListActions.initializeFindMyBookmarkProblemListsState, state => {
    const next: State = { ...state, bookmarkProblemLists: null };
    return next;
  }),

  // Find Shared Bookmark Problem Lists -------------------------------------------------------------
  on(BookmarkProblemListActions.findSharedBookmarkProblemListsSuccess, (state, { sharedBookmarkProblemLists }) => ({
    ...state,
    sharedBookmarkProblemLists
  })),
  on(BookmarkProblemListActions.initializeFindSharedBookmarkProblemListsState, state => {
    const next: State = { ...state, sharedBookmarkProblemLists: null };
    return next;
  }),

  // 問題をお気に入りリストに登録 ----------------------------------------------------------------
  on(BookmarkProblemListActions.addProblemToBookmarkProblemListSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      addProblemToBookmarkProblemListResult: { success: true, response, error: null }
    };
    return next;
  }),
  on(BookmarkProblemListActions.addProblemToBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      addProblemToBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeAddProblemToBookmarkProblemListState, state => {
    const next: State = { ...state, addProblemToBookmarkProblemListResult: null };
    return next;
  }),

  // 問題をお気に入りリストから削除 ----------------------------------------------------------------
  on(BookmarkProblemListActions.deleteProblemFromBookmarkProblemListSuccess, (state, { response, isListIdMatched, problemId }) => {
    const filteredProblems = isListIdMatched
      ? (state.bookmarkProblemListProblems ?? []).filter(problem => problem.id !== problemId)
      : state.bookmarkProblemListProblems;
    const next: State = {
      ...state,
      deleteProblemFromBookmarkProblemListResult: { success: true, response, error: null },
      bookmarkProblemListProblems: filteredProblems as GeneralProblems
    };
    return next;
  }),
  on(BookmarkProblemListActions.deleteProblemFromBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      deleteProblemFromBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeDeleteProblemFromBookmarkProblemListState, state => {
    const next: State = { ...state, deleteProblemFromBookmarkProblemListResult: null };
    return next;
  }),

  // リストに登録された問題数にプラス１をする (ステートの操作) ----------------------------------
  on(BookmarkProblemListActions.addProblemCount, (state, { listId }) => {
    const next: State = {
      ...state,
      bookmarkProblemLists: state.bookmarkProblemLists.map(list =>
        list.id === listId ? { ...list, problemCount: list.problemCount + 1 } : list
      )
    };
    return next;
  }),

  // リストに登録された問題数にマイナス１をする (ステートの操作)  -------------------------------
  on(BookmarkProblemListActions.subtractProblemCount, (state, { listId }) => {
    const next: State = {
      ...state,
      bookmarkProblemLists: state.bookmarkProblemLists.map(list =>
        list.id === listId ? { ...list, problemCount: list.problemCount - 1 } : list
      )
    };
    return next;
  }),

  // お気に入りリストを追加する (ステートの操作) -------------------------------------------------------
  on(BookmarkProblemListActions.addBookmarkProblemList, (state, { bookmarkProblemList }) => {
    const next: State = {
      ...state,
      bookmarkProblemLists: [...state.bookmarkProblemLists, { ...bookmarkProblemList }]
    };
    return next;
  }),

  // お気に入りリストの追加が成功した際、Idを上書きする (ステートの操作) --------------------------------
  on(BookmarkProblemListActions.updateBookmarkProblemListId, (state, { tempId, realId }) => ({
    ...state,
    bookmarkProblemLists: state.bookmarkProblemLists.map(p => (p.id === tempId ? { ...p, id: realId } : p))
  })),

  // お気に入りリストを削除する (ステートの操作) ------------------------------------------------------
  on(BookmarkProblemListActions.deleteBookmarkProblemListOnState, (state, { tempId }) => {
    const next: State = {
      ...state,
      bookmarkProblemLists: state.bookmarkProblemLists.filter(p => p.id !== tempId)
    };
    return next;
  }),

  // Find Bookmark Problem List Problem IDs -------------------------------------------------------
  on(BookmarkProblemListActions.findBookmarkProblemListProblemIds, state => ({ ...state, isBookmarkProblemListProblemIdsSearching: true })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemIdsSuccess, (state, { bookmarkProblemListProblemIds }) => ({
    ...state,
    matchedBookmarkProblemListProblemIds: bookmarkProblemListProblemIds,
    isBookmarkProblemListProblemIdsSearching: false
  })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemIdsFailure, state => ({
    ...state,
    isBookmarkProblemListProblemIdsSearching: false
  })),

  on(BookmarkProblemListActions.initializeFindBookmarkProblemListProblemIdsState, state => {
    const next: State = { ...state, matchedBookmarkProblemListProblemIds: null, isBookmarkProblemListProblemIdsSearching: false };
    return next;
  }),

  // お気に入りリストの作成 ----------------------------------------------------------------
  on(BookmarkProblemListActions.createBookmarkProblemListSuccess, (state, { response }) => {
    const existingList = state.bookmarkProblemLists || [];

    const updatedList = existingList.some(item => item.id === `temp-${response.name}`)
      ? existingList.map(item => (item.id === `temp-${response.name}` ? { ...item, ...response } : item))
      : [...existingList, { ...response }];

    const next: State = {
      ...state,
      createBookmarkProblemListResult: { success: true, response, error: null },
      bookmarkProblemLists: updatedList
    };

    return next;
  }),
  on(BookmarkProblemListActions.createBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      createBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeCreateBookmarkProblemListState, state => {
    const next: State = { ...state, createBookmarkProblemListResult: null };
    return next;
  }),

  // お気に入りリストの編集 ----------------------------------------------------------------
  on(BookmarkProblemListActions.updateBookmarkProblemListSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      updateBookmarkProblemListResult: { success: true, response, error: null },
      bookmarkProblemLists: state.bookmarkProblemLists.map(list => {
        return list.id === response.id
          ? {
              ...list,
              name: response.name,
              visibility: response.visibility
            }
          : list;
      })
    };
    return next;
  }),
  on(BookmarkProblemListActions.updateBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      updateBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeUpdateBookmarkProblemListState, state => {
    const next: State = { ...state, updateBookmarkProblemListResult: null };
    return next;
  }),

  // お気に入りリストの複製 ----------------------------------------------------------------
  on(BookmarkProblemListActions.copyBookmarkProblemListSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      copyBookmarkProblemListResult: { success: true, response, error: null },
      bookmarkProblemLists: [...(state.bookmarkProblemLists || []), { ...response }]
    };
    return next;
  }),
  on(BookmarkProblemListActions.copyBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      copyBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeCopyBookmarkProblemListState, state => {
    const next: State = { ...state, copyBookmarkProblemListResult: null };
    return next;
  }),

  // お気に入りリストの削除 ----------------------------------------------------------------
  on(BookmarkProblemListActions.deleteBookmarkProblemListSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      deleteBookmarkProblemListResult: { success: true, response, error: null },
      bookmarkProblemLists: state.bookmarkProblemLists.filter(list => list.id !== response.id)
    };
    return next;
  }),
  on(BookmarkProblemListActions.deleteBookmarkProblemListFailure, (state, { error }) => {
    const next: State = {
      ...state,
      deleteBookmarkProblemListResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkProblemListActions.initializeDeleteBookmarkProblemListState, state => {
    const next: State = { ...state, deleteBookmarkProblemListResult: null };
    return next;
  }),

  // お気に入りリストに登録されている問題を取得 -------------------------------------------------------
  on(BookmarkProblemListActions.findBookmarkProblemListProblems, (state, action) => ({
    ...state,
    bookmarkProblemListProblemsSearching: true
  })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemsSuccess, (state, { problems }) => ({
    ...state,
    bookmarkProblemListProblems: problems,
    bookmarkProblemListProblemsSearching: false
  })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemsFailure, state => ({
    ...state,
    bookmarkProblemListProblemsSearching: false
  })),
  on(BookmarkProblemListActions.initializeFindBookmarkProblemListProblemsState, state => {
    const next: State = { ...state, bookmarkProblemListProblems: null };
    return next;
  }),

  // お気に入りリストに登録されている問題を並び替え -------------------------------------------------------
  on(BookmarkProblemListActions.reorderBookmarkProblemListProblemsSuccess, (state, action) => {
    // 問題一覧の順番を最新の状態に変更
    const sortedProblems = [...(state.bookmarkProblemListProblems || [])].sort(
      (a, b) => action.problemIds.indexOf(a.id) - action.problemIds.indexOf(b.id)
    ) as GeneralProblems;

    return {
      ...state,
      reorderBookmarProblemListProblemsResult: action.response,
      bookmarkProblemListProblems: sortedProblems
    };
  }),

  on(BookmarkProblemListActions.reorderBookmarkProblemListProblemsFailure, state => ({
    ...state,
    reorderBookmarProblemListProblemsResult: { success: false }
  })),
  on(BookmarkProblemListActions.initializeReorderBookmarkProblemListProblemsState, state => {
    const next: State = { ...state, reorderBookmarProblemListProblemsResult: null };
    return next;
  }),

  // お気に入りリストに登録されている問題の印刷用データの取得 --------------------------------------------------------
  on(BookmarkProblemListActions.findBookmarkProblemListProblemImages, state => ({ ...state, bookmarkProblemListProblemsSearching: true })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemImagesSuccess, (state, { problems }) => ({
    ...state,
    bookmarkProblemListProblems: problems,
    bookmarkProblemListProblemsSearching: false
  })),
  on(BookmarkProblemListActions.findBookmarkProblemListProblemImagesFailure, state => ({
    ...state,
    bookmarkProblemListProblemsSearching: false
  })),
  on(BookmarkProblemListActions.initializeFindBookmarkProblemListProblemImagesState, state => {
    const next: State = { ...state, bookmarkProblemListProblems: null };
    return next;
  })
);

export function reducer(state: State | undefined, action: Action) {
  return BookmarkProblemListReducer(state, action);
}
